import React, { useEffect, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../components/ui/popover";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Check, ChevronsUpDown, X } from "lucide-react";
import {
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "../../components/ui/form";
import { cn } from "../../lib/utils";
import XanoAPI from "../../lib/XanoAPI";
import { ClientType, RelatedClientMeta } from "../../types";
import { useAuth } from "../../lib/AuthContext";

interface ClientLookupControllerProps {
  name: string;
  label: string;
  defaultValue?: number | null;
  infoMessage?: string;
  inline?: boolean;
  currentClientId?: number | string; // Exclude current client from list
  relatedClientMeta?: RelatedClientMeta[]; // Pre-loaded metadata for already selected client (limited fields)
  onChange?: (value: number | null) => void;
}

const ClientLookupController: React.FC<ClientLookupControllerProps> = ({
  name,
  label,
  defaultValue = null,
  infoMessage,
  inline,
  currentClientId,
  relatedClientMeta,
  onChange,
}) => {
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useFormContext();
  
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState<ClientType[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientType | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const api = XanoAPI.getInstance();
  const auth = useAuth();
  
  // Watch the form value to trigger re-renders when it changes
  const currentFormValue = watch(name);

  // Check if current user is admin
  const isAdmin = auth?.currentUser?.user_permissions?.[0]?.role === "admin";
  const currentUserId = auth?.currentUser?.id;
  const [clientsLoaded, setClientsLoaded] = useState(false);

  // Lazy-load clients only when dropdown is opened
  const fetchClients = async () => {
    if (clientsLoaded) return; // Already loaded
    
    setLoading(true);
    try {
      // Determine user filter: agents see only their clients, admins see all
      const userFilter = isAdmin ? undefined : currentUserId?.toString();
      
      // ⭐ NEW: Use optimized single-call API (fetches 1000 clients at once)
      let allClients = await api.getClientsForLookup(userFilter);
      
      // Filter out current client if provided
      if (currentClientId) {
        allClients = allClients.filter(
          (client) => client.id?.toString() !== currentClientId?.toString()
        );
      }
      
      // Sort by last name, first name
      allClients.sort((a, b) => {
        const aName = `${a.lastName || ''} ${a.firstName || ''}`.toLowerCase();
        const bName = `${b.lastName || ''} ${b.firstName || ''}`.toLowerCase();
        return aName.localeCompare(bName);
      });
      
      setClients(allClients);
      setClientsLoaded(true);
      console.log(`✅ Loaded ${allClients.length} clients for lookup (Admin: ${isAdmin})`);
    } catch (error) {
      console.error("Error fetching clients for lookup:", error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  // Trigger fetch when dropdown opens
  useEffect(() => {
    if (open && !clientsLoaded) {
      fetchClients();
    }
  }, [open, clientsLoaded]);

  // Initialize selected client from metadata (instant) or from clients list (after loading)
  useEffect(() => {
    const currentValue = getValues(name) || defaultValue;
    
    if (!currentValue) {
      setSelectedClient(null);
      setValue(name, null);
      return;
    }

    // ⭐ PRIORITY 1: Use metadata if available (instant, no API call needed)
    if (relatedClientMeta && relatedClientMeta.length > 0) {
      const metaClient = relatedClientMeta[0];
      if (metaClient.id?.toString() === currentValue?.toString()) {
        console.log('✅ Using related_client_id_meta for instant display:', metaClient.firstName, metaClient.lastName);
        setSelectedClient(metaClient);
        setValue(name, currentValue);
        return;
      }
    }

    // PRIORITY 2: Fall back to searching loaded clients (if dropdown was opened)
    if (clients.length > 0) {
      const client = clients.find(
        (c) => c.id?.toString() === currentValue?.toString()
      );
      if (client) {
        console.log('Using client from loaded list:', client.firstName, client.lastName);
        setSelectedClient(client);
      } else {
        // Client ID exists but not found in loaded list
        setSelectedClient(null);
      }
    }
    
    setValue(name, currentValue);
  }, [defaultValue, name, setValue, getValues, clients, relatedClientMeta]);

  const handleSelect = (clientId: string) => {
    const client = clients.find((c) => c.id?.toString() === clientId);
    if (client) {
      console.log('handleSelect - Setting related_client_id to:', Number(client.id));
      setSelectedClient(client);
      setValue(name, Number(client.id), { shouldDirty: true, shouldValidate: true });
      console.log('Form value set, current value:', getValues(name));
      if (onChange) onChange(Number(client.id));
    }
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    console.log('🗑️ Clearing related_client_id');
    setSelectedClient(null);
    setValue(name, null, { shouldDirty: true, shouldValidate: true });
    if (onChange) onChange(null);
  };

  const getDisplayText = () => {
    // Use watched value to ensure component re-renders on changes
    const currentValue = currentFormValue;
    
    // If value is null/undefined, always show placeholder
    if (!currentValue) {
      return `Select ${label}`;
    }

    // PRIORITY 2: Use selectedClient if available AND ID matches
    if (selectedClient && selectedClient.id?.toString() === currentValue?.toString()) {
      return `${selectedClient.firstName} ${selectedClient.lastName}${
        selectedClient.id ? ` (ID: ${selectedClient.id})` : ''
      }`;
    }

    // PRIORITY 3: Fall back to metadata ONLY if there's a value but no selectedClient yet
    // This handles the initial load case
    if (relatedClientMeta && relatedClientMeta.length > 0) {
      const metaClient = relatedClientMeta[0];
      // Double-check the ID matches
      if (metaClient.id?.toString() === currentValue?.toString()) {
        return `${metaClient.firstName} ${metaClient.lastName}${
          metaClient.id ? ` (ID: ${metaClient.id})` : ''
        }`;
      }
    }

    // PRIORITY 4: Show loading state only when dropdown is open and we're fetching
    if (loading) return 'Loading clients...';
    
    // Last resort: show ID
    return `Client ID: ${currentValue}`;
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ fieldState: { error } }) => (
        <FormItem className={`mb-6 ${inline ? "flex items-center" : ""}`}>
          <FormLabel htmlFor={name} className="w-1/3">
            {label}
          </FormLabel>
          <FormControl className="w-2/3">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between hover:bg-white"
                    type="button"
                  >
                    <span className="text-sm text-gray-800 truncate">
                      {getDisplayText()}
                    </span>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      {currentFormValue && (
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={handleClear}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleClear(e as any);
                            }
                          }}
                          className="rounded-sm opacity-70 hover:opacity-100 hover:bg-red-100 p-0.5 transition-colors cursor-pointer"
                          aria-label="Clear selection"
                        >
                          <X className="h-4 w-4 text-gray-600 hover:text-red-600" />
                        </div>
                      )}
                      <ChevronsUpDown className="h-4 w-4 opacity-50" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <div className="flex flex-col">
                    {/* Search Input */}
                    <div className="p-2 border-b">
                      <Input
                        placeholder="Search by name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="h-9"
                      />
                    </div>
                    
                    {/* Client List */}
                    <div className="max-h-[300px] overflow-y-auto p-1">
                      {loading ? (
                        <div className="p-4 text-sm text-center text-muted-foreground">
                          Loading clients...
                        </div>
                      ) : (() => {
                        // Filter clients based on search query
                        const filteredClients = clients.filter((client) => {
                          if (!searchQuery) return true;
                          const query = searchQuery.toLowerCase();
                          const fullName = `${client.firstName || ''} ${client.lastName || ''}`.toLowerCase();
                          const email = (client.email || '').toLowerCase();
                          const id = client.id?.toString() || '';
                          return fullName.includes(query) || email.includes(query) || id.includes(query);
                        });
                        
                        if (filteredClients.length === 0) {
                          return (
                            <div className="p-4 text-sm text-center text-muted-foreground">
                              No clients found.
                            </div>
                          );
                        }
                        
                        return filteredClients.map((client) => {
                          const isSelected = selectedClient?.id?.toString() === client.id?.toString();
                          const displayName = `${client.firstName} ${client.lastName}`;
                          const subInfo = [
                            client.email,
                            client.type?.toUpperCase(),
                          ]
                            .filter(Boolean)
                            .join(" • ");

                          return (
                            <div
                              key={client.id}
                              onClick={() => {
                                console.log('Client clicked:', client.id);
                                handleSelect(client.id!.toString());
                              }}
                              className={cn(
                                "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors",
                                "hover:bg-accent hover:text-white",
                                isSelected && "bg-accent text-white"
                              )}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  isSelected ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">{displayName}</span>
                                {subInfo && (
                                  <span className={cn(
                                    "text-xs",
                                    isSelected || "text-muted-foreground"
                                  )}>
                                    {subInfo} {client.id && `(ID: ${client.id})`}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </PopoverContent>
            </Popover>
          </FormControl>
          {error && (
            <FormMessage className="text-red-500">{error.message}</FormMessage>
          )}
          {!error && infoMessage && (
            <FormMessage className="text-gray-500 text-xs font-normal">
              {infoMessage}
            </FormMessage>
          )}
        </FormItem>
      )}
    />
  );
};

export default ClientLookupController;

